/**
 * Virtual Size Picker
 * -------------------
 * Splits a single Shopify "Size" option like "30/32" or "S / 30" into two UI rows:
 *   - Waist (or Size, for letter primaries)
 *   - Length
 *
 * Shopify still only has ONE option value (e.g. "35 / 34"). This custom element:
 *   1. Reads a waist/length → optionValueId map from Liquid (`[data-virtual-size-map]`)
 *   2. Lets the shopper pick waist + length separately
 *   3. Checks the matching hidden radio so the theme variant-picker / Section Rendering
 *      API updates price, media, and ATC as usual
 *
 * Important behaviours:
 *   - Out-of-stock (OOS) combos stay selectable (strike + bell) for Klaviyo BIS
 *   - Combos that do NOT exist for the selected waist are hidden
 *   - We never set `disabled` on length buttons: theme CSS
 *     `:disabled + .block-swatch { display: none }` would hide the *next* swatch
 *     (e.g. disabled "32" hid "34" when waist 35 was selected)
 */

(() => {
  const TAG_NAME = 'virtual-size-picker';

  class VirtualSizePicker extends HTMLElement {
    /**
     * Runs when the element is inserted into the DOM (and again after Section
     * Rendering morphs replace it). Wire up listeners once; re-read state every time.
     */
    connectedCallback() {
      this._syncFromDom();

      if (!this._boundClick) {
        this._boundClick = this._onClick.bind(this);
        this.addEventListener('click', this._boundClick);
      }

      // After variant change, Impact re-renders the product section. Re-sync map + UI.
      if (!this._boundRerender && this._form) {
        this._boundRerender = () => {
          requestAnimationFrame(() => {
            if (!this.isConnected) return;
            this._syncFromDom();
          });
        };
        this._form.addEventListener('product:rerender', this._boundRerender);
      }
    }

    disconnectedCallback() {
      if (this._boundRerender && this._form) {
        this._form.removeEventListener('product:rerender', this._boundRerender);
        this._boundRerender = null;
      }
    }

    /**
     * Pull latest config from DOM attributes + JSON map, then refresh buttons / ATC.
     */
    _syncFromDom() {
      this._variantPicker = this.closest('variant-picker');
      this._optionRoot = this.closest('fieldset') || this._variantPicker;
      this._formId = this._variantPicker?.getAttribute('form-id');
      this._form = this._formId ? document.forms[this._formId] : null;
      this._paramName = this.dataset.paramName;

      // Map entries: { waist, length, id (option value id), available }
      this._map = this._normalizeMap(this._readSizeMap());

      this._hideSoldOut = this.dataset.hideSoldOut === 'true';
      this._soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
      this._addToCartLabel = this.dataset.addToCartLabel || 'Add to cart';
      this._unavailableLabel = this.dataset.unavailableLabel || 'Unavailable';
      this._selectedWaist = this._normalizeSizeToken(this.dataset.selectedWaist);
      this._selectedLength = this._normalizeSizeToken(this.dataset.selectedLength);

      this._ensureValidLengthSelection();
      this._refreshUi();
      this._selectVariant();
    }

    /**
     * Prefer the Liquid JSON map (built from option.values). If parse fails / empty,
     * rebuild from hidden radios that carry data-virtual-waist / data-virtual-length.
     */
    _readSizeMap() {
      let map = [];

      try {
        map = JSON.parse(this.querySelector('[data-virtual-size-map]')?.textContent || '[]');
      } catch (error) {
        map = [];
      }

      if (Array.isArray(map) && map.length) return map;

      const inputs = this._optionRoot?.querySelectorAll('[data-virtual-waist][data-virtual-length]') || [];
      return Array.from(inputs).map((input) => ({
        waist: input.getAttribute('data-virtual-waist'),
        length: input.getAttribute('data-virtual-length'),
        id: input.getAttribute('data-virtual-option-value-id') || input.value,
        available: input.dataset.available !== 'false',
      }));
    }

    /**
     * Normalize "35 / 34", NBSP, etc. → "35" / "34" so Liquid + JS comparisons match.
     */
    _normalizeSizeToken(value) {
      return String(value ?? '')
        .replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
        .trim()
        .replace(/\s+/g, '');
    }

    _normalizeMap(map) {
      return (map || []).map((entry) => ({
        ...entry,
        waist: this._normalizeSizeToken(entry.waist),
        length: this._normalizeSizeToken(entry.length),
        id: String(entry.id ?? ''),
        available: Boolean(entry.available),
      }));
    }

    _onClick(event) {
      const button = event.target.closest('[data-virtual-dimension]');
      if (
        !button ||
        button.disabled ||
        button.getAttribute('aria-disabled') === 'true' ||
        button.classList.contains('is-unavailable')
      ) {
        return;
      }

      const dimension = button.dataset.virtualDimension;
      const value = this._normalizeSizeToken(button.dataset.virtualValue);

      if (dimension === 'waist') {
        this._selectedWaist = value;
        // Current length may not exist for the new waist → pick a valid one (prefer in-stock).
        this._ensureValidLengthSelection();
      } else {
        this._selectedLength = value;
      }

      this._refreshUi();
      this._selectVariant();
    }

    /**
     * If waist+length is not a real combo, auto-select the first existing length
     * for that waist (in-stock first, otherwise first OOS).
     */
    _ensureValidLengthSelection() {
      if (this._comboExists(this._selectedWaist, this._selectedLength)) return;

      const nextLength = this._firstExistingLength(this._selectedWaist);
      this._selectedLength = nextLength ? this._normalizeSizeToken(nextLength) : '';
    }

    /** True when admin has this exact waist/length option value (even if stock is 0). */
    _comboExists(waist, length) {
      const waistValue = this._normalizeSizeToken(waist);
      const lengthValue = this._normalizeSizeToken(length);
      if (!waistValue || !lengthValue) return false;
      return this._map.some((entry) => entry.waist === waistValue && entry.length === lengthValue);
    }

    _comboAvailable(waist, length) {
      const entry = this._currentEntryFor(waist, length);
      return entry?.available ?? false;
    }

    _currentEntryFor(waist, length) {
      const waistValue = this._normalizeSizeToken(waist);
      const lengthValue = this._normalizeSizeToken(length);
      return this._map.find((item) => item.waist === waistValue && item.length === lengthValue);
    }

    _firstExistingLength(waist) {
      const waistValue = this._normalizeSizeToken(waist);
      const forWaist = this._map.filter((entry) => entry.waist === waistValue);
      const available = forWaist.find((entry) => entry.available);
      if (available) return available.length;
      return forWaist[0]?.length || '';
    }

    _currentEntry() {
      return this._currentEntryFor(this._selectedWaist, this._selectedLength);
    }

    /** Find the hidden native radio for this Shopify option value id. */
    _findOptionInput(optionValueId) {
      const id = String(optionValueId || '');
      if (!id) return null;

      return (
        document.getElementById(`${this._paramName}-${id}`) ||
        this._optionRoot?.querySelector(`[data-virtual-option-value-id="${CSS.escape(id)}"]`) ||
        this._variantPicker?.querySelector(`[data-virtual-option-value-id="${CSS.escape(id)}"]`) ||
        Array.from(this._form?.elements || []).find(
          (item) => item?.getAttribute?.('data-virtual-option-value-id') === id
        ) ||
        null
      );
    }

    /**
     * Check the matching hidden radio → theme variant-picker handles the rest.
     * Skip dispatch if already checked to avoid product:rerender loops.
     */
    _selectVariant() {
      const entry = this._currentEntry();
      this._syncBuyButton(entry);

      if (!entry) return;

      const input = this._findOptionInput(entry.id);
      if (!input || input.checked) return;

      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Mirror ATC label/state for the virtual selection.
     * OOS stays enabled so Klaviyo back-in-stock can hook the click.
     */
    _syncBuyButton(entry) {
      if (!this._form) return;

      const submitButtons = Array.from(
        this._form.querySelectorAll('buy-buttons [type="submit"], [type="submit"]')
      );
      if (!submitButtons.length) return;

      let label = this._addToCartLabel;
      let disabled = false;

      if (!entry) {
        label = this._unavailableLabel;
        disabled = true;
      } else if (!entry.available) {
        label = this._soldOutLabel;
        disabled = false;
      }

      submitButtons.forEach((submitButton) => {
        submitButton.disabled = disabled;

        const labelNode =
          submitButton.querySelector('button-content') ||
          submitButton.querySelector('.button__content') ||
          submitButton;

        if (labelNode.hasAttribute?.('rotated-text')) {
          labelNode.setAttribute('rotated-text', label);
        }
        if (labelNode.hasAttribute?.('up-text')) {
          labelNode.setAttribute('up-text', label);
        }

        labelNode.textContent = label;
      });
    }

    _refreshUi() {
      this._sortDimensionButtons('waist');
      this._sortDimensionButtons('length');
      this._refreshWaistButtons();
      this._refreshLengthButtons();
      this._updateWaistSelection();
      this._updateLengthSelection();
      this.dataset.selectedWaist = this._selectedWaist;
      this.dataset.selectedLength = this._selectedLength;
    }

    /** Numeric sizes ascending; letter sizes (XS–XXXXL) in fashion order. */
    _compareSizeValues(a, b) {
      const aValue = String(a || '');
      const bValue = String(b || '');
      const aNum = Number(aValue);
      const bNum = Number(bValue);
      const aIsNum = aValue !== '' && !Number.isNaN(aNum);
      const bIsNum = bValue !== '' && !Number.isNaN(bNum);

      if (aIsNum && bIsNum) return aNum - bNum;
      if (aIsNum) return -1;
      if (bIsNum) return 1;

      const order = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'XXXXL'];
      const aIndex = order.indexOf(aValue.toUpperCase());
      const bIndex = order.indexOf(bValue.toUpperCase());
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;

      return aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' });
    }

    /** In-stock first, then OOS — each group sorted naturally. */
    _sortDimensionButtons(dimension) {
      const container =
        dimension === 'waist'
          ? this.querySelector('[data-virtual-waist-values]')
          : this.querySelector('[data-virtual-length-row] .virtual-size-picker__values');
      if (!container) return;

      const buttons = Array.from(container.querySelectorAll(`[data-virtual-dimension="${dimension}"]`));
      buttons.sort((a, b) => {
        const aValue = a.dataset.virtualValue;
        const bValue = b.dataset.virtualValue;
        const aAvailable =
          dimension === 'waist' ? this._waistHasAvailable(aValue) : this._comboAvailable(this._selectedWaist, aValue);
        const bAvailable =
          dimension === 'waist' ? this._waistHasAvailable(bValue) : this._comboAvailable(this._selectedWaist, bValue);

        if (aAvailable !== bAvailable) return aAvailable ? -1 : 1;
        return this._compareSizeValues(aValue, bValue);
      });

      buttons.forEach((button) => container.appendChild(button));
    }

    _updateWaistSelection() {
      this.querySelectorAll('[data-virtual-dimension="waist"]').forEach((button) => {
        button.classList.toggle(
          'is-selected',
          this._normalizeSizeToken(button.dataset.virtualValue) === this._selectedWaist
        );
      });
    }

    _updateLengthSelection() {
      this.querySelectorAll('[data-virtual-dimension="length"]').forEach((button) => {
        button.classList.toggle(
          'is-selected',
          this._normalizeSizeToken(button.dataset.virtualValue) === this._selectedLength
        );
      });
    }

    /** Waist is "in stock" if ANY length for that waist is available. */
    _waistHasAvailable(waist) {
      const waistValue = this._normalizeSizeToken(waist);
      return this._map.some((entry) => entry.waist === waistValue && entry.available);
    }

    _refreshWaistButtons() {
      this.querySelectorAll('[data-virtual-dimension="waist"]').forEach((button) => {
        const waist = this._normalizeSizeToken(button.dataset.virtualValue);
        const hasAvailable = this._waistHasAvailable(waist);
        const isSelected = waist === this._selectedWaist;

        // is-disabled = OOS visual (strike + bell); still clickable.
        button.classList.toggle('is-disabled', !hasAvailable);
        button.disabled = false;

        if (this._hideSoldOut) {
          button.hidden = !hasAvailable && !isSelected;
        } else {
          button.hidden = false;
        }
      });
    }

    /**
     * Length rules for the selected waist:
     *   - exists + available     → normal swatch
     *   - exists + OOS           → is-disabled (visible, selectable)
     *   - does not exist         → hide (not the same as OOS)
     *
     * Never use the HTML `disabled` attribute here — see file header.
     */
    _refreshLengthButtons() {
      let visibleCount = 0;

      this.querySelectorAll('[data-virtual-dimension="length"]').forEach((button) => {
        const length = this._normalizeSizeToken(button.dataset.virtualValue);
        const exists = this._comboExists(this._selectedWaist, length);
        const available = this._comboAvailable(this._selectedWaist, length);
        const isSelected = length === this._selectedLength;

        button.classList.toggle('is-disabled', exists && !available);
        button.classList.toggle('is-unavailable', !exists);
        button.disabled = false;
        button.setAttribute('aria-disabled', exists ? 'false' : 'true');

        button.hidden = !exists || (this._hideSoldOut && !available && !isSelected);

        if (!button.hidden) visibleCount += 1;
      });

      // Hide entire Length row (label included) when no lengths exist for this waist.
      const lengthRow = this.querySelector('[data-virtual-length-row]');
      if (lengthRow) {
        const isEmpty = visibleCount === 0;
        lengthRow.hidden = isEmpty;
        lengthRow.classList.toggle('virtual-size-picker__row--empty', isEmpty);
      }
    }
  }

  const existing = window.customElements.get(TAG_NAME);

  if (!existing) {
    window.customElements.define(TAG_NAME, VirtualSizePicker);
  } else {
    // Theme editor / live reload can re-inject this file while the tag is already defined.
    // Patch methods onto the registered class and re-sync open pickers.
    const proto = existing.prototype;
    Object.getOwnPropertyNames(VirtualSizePicker.prototype).forEach((key) => {
      if (key === 'constructor') return;
      Object.defineProperty(proto, key, Object.getOwnPropertyDescriptor(VirtualSizePicker.prototype, key));
    });

    document.querySelectorAll(TAG_NAME).forEach((node) => {
      if (typeof node._syncFromDom === 'function') node._syncFromDom();
    });
  }
})();
